import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Context and Theme
import { AppProvider } from './context/AppContext';
import theme from './styles/theme';

// Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import AppLayout from './components/navigation/AppLayout';

// Pages
import LoginPage from './pages/auth/LoginPage';
import LoginOTPPage from './pages/auth/LoginOTPPage';
import RegisterPage from './pages/auth/RegisterPage';
import CheckEmailPage from './pages/auth/CheckEmailPage';
import EmailVerifiedPage from './pages/auth/EmailVerifiedPage';
import PendingApprovalPage from './pages/auth/PendingApprovalPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import VerifyOTPPage from './pages/auth/VerifyOTPPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ResendVerificationPage from './pages/auth/ResendVerificationPage';
import DashboardPage from './pages/DashboardPage';
import ClubsPage from './pages/ClubsPage';
import ClubDetailsPage from './pages/ClubDetailsPage';
import FeedPage from './pages/FeedPage';
import InsiderFeedPage from './pages/InsiderFeedPage';
import ClubDiscussionsPage from './pages/ClubDiscussionsPage';
import DiscussionThreadPage from './pages/DiscussionThreadPage';
import CreateDiscussionPage from './pages/CreateDiscussionPage';
import SearchPage from './pages/SearchPage';
import UserProfilePage from './pages/UserProfilePage';
import ProfilePage from './pages/ProfilePage';
import QuizPage from './pages/QuizPage';
import MyApplicationsPage from './pages/MyApplicationsPage';
import StudentApprovalPage from './pages/StudentApprovalPage';
import ClubManagementPage from './pages/ClubManagementPage';
import CreateClubPage from './pages/CreateClubPage';
import ManageClubPage from './pages/ManageClubPage';
import AIMentorPage from './pages/AIMentorPage';
import ApplyAmbassadorPage from './pages/ApplyAmbassadorPage';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppProvider>
          <Router>
            <Routes>
              {/* Public routes - redirect to dashboard if authenticated */}
              <Route
                path="/login"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <LoginPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <RegisterPage />
                  </ProtectedRoute>
                }
              />

              {/* Email verification routes - public access */}
              <Route
                path="/check-email"
                element={<CheckEmailPage />}
              />
              <Route
                path="/email-verified"
                element={<EmailVerifiedPage />}
              />
              <Route
                path="/verify-email"
                element={<VerifyEmailPage />}
              />
              <Route
                path="/verify-otp"
                element={<VerifyOTPPage />}
              />
              <Route
                path="/login-otp"
                element={<LoginOTPPage />}
              />
              <Route
                path="/resend-verification"
                element={<ResendVerificationPage />}
              />

              {/* Password reset routes - public access */}
              <Route
                path="/forgot-password"
                element={<ForgotPasswordPage />}
              />
              <Route
                path="/reset-password"
                element={<ResetPasswordPage />}
              />

              {/* Application submitted route - for both verified and unverified users */}
              {/* Used for registration approval (unverified) and club application submission (verified) */}
              <Route
                path="/pending-approval"
                element={
                  <PendingApprovalPage />
                }
              />

              {/* Protected routes - require authentication and verification */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <AppLayout>
                      <DashboardPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Placeholder routes for future implementation */}
              <Route
                path="/clubs"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <AppLayout>
                      <ClubsPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/clubs/:clubId"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <AppLayout>
                      <ClubDetailsPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/clubs/:clubId/feed"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <InsiderFeedPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/clubs/:clubId/discussions"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <AppLayout>
                      <ClubDiscussionsPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/clubs/:clubId/discussions/:discussionId"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <AppLayout>
                      <DiscussionThreadPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/clubs/:clubId/create-discussion"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <AppLayout>
                      <CreateDiscussionPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/clubs/:clubId/manage"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true} facultyOnly={true}>
                    <AppLayout>
                      <ManageClubPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/feed"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <AppLayout>
                      <FeedPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/search"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <AppLayout>
                      <SearchPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users/:userId"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <AppLayout>
                      <UserProfilePage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <AppLayout>
                      <ProfilePage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/ai-mentor"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <AIMentorPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/clubs/:clubId/quiz"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <AppLayout>
                      <QuizPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/create-post"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true}>
                    <AppLayout>
                      <FeedPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/my-applications"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true} studentOnly={true}>
                    <AppLayout>
                      <MyApplicationsPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/create-club"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true} facultyOnly={true}>
                    <AppLayout>
                      <CreateClubPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/student-approval"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true} facultyOnly={true}>
                    <AppLayout>
                      <StudentApprovalPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/club-management"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true} facultyOnly={true}>
                    <AppLayout>
                      <ClubManagementPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/apply-ambassador"
                element={
                  <ProtectedRoute requireAuth={true} requireVerification={true} studentOnly={true}>
                    <AppLayout>
                      <ApplyAmbassadorPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Default redirect - redirect to clubs (main landing page after login) */}
              <Route path="/" element={<Navigate to="/clubs" replace />} />

              {/* Catch all route - redirect to clubs */}
              <Route path="*" element={<Navigate to="/clubs" replace />} />
            </Routes>
          </Router>
        </AppProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;